import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  CheckCircle2,
  Clock,
  Download,
  FileSpreadsheet,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listSubscriptionPlans,
  listFoodPreferences,
} from "@/lib/admin/meal-plans-v2.functions";
import { Textarea } from "@/components/ui/textarea";
import {
  listWeeklyCycles,
  getWeeklyCycle,
  getWeeklyCycleHeader,
  updateWeeklyCycleHeader,
  updateWeeklyCycleIdentity,
  createWeeklyCycleHeader,
  addSlotDish,
  removeSlotDish,
  reorderSlotDishes,
  listMenuItems,
  replaceMenuItems,
  listPlanImportCandidates,
  publishWeeklyCycle,
  scheduleWeeklyCycle,
  withdrawWeeklyCycle,
  publishWeeklyCycleHeader,
  withdrawWeeklyCycleHeader,
  archiveWeeklyCycle,
} from "@/lib/admin/weekly-menu.functions";

import {
  downloadCompleteWeeklyMenuTemplate,
  downloadMenuItemsTemplate,
  exportMenuItems,
  parseMenuItemsFile,
  assembleImportCandidates,
  isWeeklyOffDay,

  type MenuItemSlot,
  type MenuItemMatch,
  type SkippedMenuItemRow,
} from "@/lib/admin/weekly-menu-items";
import { getPublicPlanDishes } from "@/lib/public/meal-plans-v2-dishes.functions";

/** Move an item within an array, returning a new array. */
function moveItem<T>(arr: T[], from: number, to: number): T[] {
  const next = arr.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it!);
  return next;
}

export const Route = createFileRoute("/_admin/admin/weekly-menu")({
  component: WeeklyMenuAdmin,
});

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOTS = ["breakfast", "lunch", "snacks", "dinner"] as const;
type Slot = (typeof SLOTS)[number];

function isoMonday(d: Date): string {
  const day = d.getDay(); // 0=Sun
  const diff = (day + 6) % 7; // days since Monday
  const mon = new Date(d);
  mon.setDate(d.getDate() - diff);
  return mon.toISOString().slice(0, 10);
}
function monthStartOf(iso: string): string {
  return iso.slice(0, 7) + "-01";
}
function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function statusVariant(s: string): "default" | "secondary" | "outline" | "destructive" {
  if (s === "published") return "default";
  if (s === "scheduled") return "secondary";
  if (s === "withdrawn") return "destructive";
  return "outline";
}

function cycleActionForStatus(status: string): "publish" | "withdraw" | null {
  if (status === "draft" || status === "withdrawn") return "publish";
  if (status === "published") return "withdraw";
  return null;
}

type CycleRow = {
  cycle_id: string;
  plan_id: string;
  plan_slug: string;
  plan_title: string;
  food_preference_code: string;
  week_start_date: string;
  week_end_date: string;
  month_label: string;
  pub_status: string;
  publish_at: string | null;
  published_at: string | null;
  is_demo: boolean;
  updated_at: string;
};

type SlotDish = { dish_id: string; name?: string | null; sort_order?: number; is_primary?: boolean };

type CycleDetail = {
  cycle_id: string;
  plan_id: string;
  plan_slug?: string;
  plan_title?: string;
  food_preference_code: string;
  week_start_date: string;
  week_end_date: string;
  pub_status: string;
  publish_at?: string | null;
  published_at?: string | null;
  cells?: Array<{
    day_of_week: number; // 1..7 (Mon..Sun) or 0..6 depending on RPC
    slot: Slot;
    dish_id: string | null;
    dish_name?: string | null;
  }>;
  days?: Array<{
    day_of_week: number;
    slots: Record<Slot, { dish_id: string | null; dish_name?: string | null }>;
    slot_dishes?: Partial<Record<Slot, SlotDish[]>>;
  }>;
};

type Grid = Record<number, Record<Slot, SlotDish[]>>;

function emptyGrid(): Grid {
  const grid: Grid = {};
  for (let d = 0; d < 7; d++) {
    grid[d] = { breakfast: [], lunch: [], snacks: [], dinner: [] };
  }
  return grid;
}

/** Normalise the JSON RPC payload into a stable [day 0..6][slot] dish list. */
function normaliseGrid(detail: CycleDetail | null): Grid {
  const grid = emptyGrid();
  if (!detail) return grid;
  // The RPC may return day_of_week as 0..6 (Mon..Sun) or 1..7. Detect the base
  // from the payload itself — assuming 1-based shifts every day by one and
  // collapses two days into the same column.
  const raws = [
    ...(Array.isArray(detail.days) ? detail.days.map((d) => Number(d.day_of_week)) : []),
    ...(Array.isArray(detail.cells) ? detail.cells.map((c) => Number(c.day_of_week)) : []),
  ].filter((n) => Number.isFinite(n));
  const oneBased = raws.length > 0 && Math.min(...raws) >= 1 && Math.max(...raws) >= 7;
  const toIdx = (raw: number) =>
    Math.max(0, Math.min(6, oneBased ? raw - 1 : raw));


  if (Array.isArray(detail.days)) {
    for (const day of detail.days) {
      const idx = toIdx(Number(day.day_of_week));
      for (const s of SLOTS) {
        const list = day.slot_dishes?.[s];
        if (Array.isArray(list) && list.length > 0) {
          grid[idx][s] = list
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((d) => ({ dish_id: d.dish_id, name: d.name ?? null, sort_order: d.sort_order }));
          continue;
        }
        const cell = day.slots?.[s] as
          | { dish_id: string | null; dish_name?: string | null; name?: string | null }
          | undefined;
        if (cell?.dish_id) {
          grid[idx][s] = [{ dish_id: cell.dish_id, name: cell.dish_name ?? cell.name ?? null }];
        }
      }
    }
  } else if (Array.isArray(detail.cells)) {
    for (const c of detail.cells) {
      const idx = toIdx(Number(c.day_of_week));
      if (SLOTS.includes(c.slot) && c.dish_id) {
        grid[idx][c.slot].push({ dish_id: c.dish_id, name: c.dish_name ?? null });
      }
    }
  }
  return grid;
}


function WeeklyMenuAdmin() {
  const qc = useQueryClient();

  const [month, setMonth] = useState<string>(() => monthStartOf(isoMonday(new Date())));
  const [planId, setPlanId] = useState<string | null>(null);
  const [preference, setPreference] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState<string>(() => isoMonday(new Date()));
  const [activeCycleId, setActiveCycleId] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<"details" | "header" | "items">("details");
  const [closedSlots, setClosedSlots] = useState<Set<string>>(() => new Set());
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CycleRow | null>(null);
  const [newMonthLabel, setNewMonthLabel] = useState<string>("");
  const [newNotes, setNewNotes] = useState<string>("");
  const [newWeekOpen, setNewWeekOpen] = useState(false);
  // Expected creation failures are shown inside the Add New Week dialog; the
  // dialog stays open and keeps everything the admin typed.
  const [createError, setCreateError] = useState<string | null>(null);
  // Import feedback belongs to exactly one cycle: it is tagged with the cycle it
  // was produced for so it can never be shown while another week is open.
  const [importState, setImportState] = useState<
    {
      cycleId: string;
      errors: string[];
      matches: MenuItemMatch[];
      skipped: SkippedMenuItemRow[];
      imported: number | null;
    } | null
  >(null);
  const menuItemsFileRef = useRef<HTMLInputElement | null>(null);

  function openEditor(id: string, mode: "details" | "header" | "items" = "details") {
    // A validation message always belongs to one cycle: opening another cycle
    // (or reopening this one) starts with a clean error state.
    setActionError(null);
    setImportState(null);
    setActiveCycleId(id);
    setEditorMode(mode);
    // Defer to next frame so the editor block is in the DOM before scrolling.
    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function switchEditorMode(mode: "details" | "header" | "items") {
    setActionError(null);
    setImportState(null);
    setEditorMode(mode);
  }


  function toggleSlotClosed(dayIdx: number, slot: Slot, hasDishes: boolean) {
    const key = `${dayIdx}-${slot}`;
    setClosedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      if (hasDishes) {
        toast.error(
          "Please remove the dishes in this meal slot before marking it closed / not serving.",
        );
        return prev;
      }
      next.add(key);
      return next;
    });
  }


  const listPlansFn = useServerFn(listSubscriptionPlans);
  const listPrefsFn = useServerFn(listFoodPreferences);
  const listCyclesFn = useServerFn(listWeeklyCycles);
  const getCycleFn = useServerFn(getWeeklyCycle);
  const createHeaderFn = useServerFn(createWeeklyCycleHeader);
  const publishFn = useServerFn(publishWeeklyCycle);
  const scheduleFn = useServerFn(scheduleWeeklyCycle);
  const withdrawFn = useServerFn(withdrawWeeklyCycle);
  const archiveFn = useServerFn(archiveWeeklyCycle);
  const getPlanDishesFn = useServerFn(getPublicPlanDishes);
  const addDishFn = useServerFn(addSlotDish);
  const removeDishFn = useServerFn(removeSlotDish);
  const reorderDishesFn = useServerFn(reorderSlotDishes);
  const listItemsFn = useServerFn(listMenuItems);
  const replaceItemsFn = useServerFn(replaceMenuItems);
  const importCandidatesFn = useServerFn(listPlanImportCandidates);

  const plansQ = useQuery({
    queryKey: ["mp2-admin-plans"],
    queryFn: () => listPlansFn(),
    staleTime: 60_000,
  });
  const prefsQ = useQuery({
    queryKey: ["mp2-admin-prefs"],
    queryFn: () => listPrefsFn(),
    staleTime: 60_000,
  });
  const cyclesQ = useQuery({
    queryKey: ["mp2-weekly-cycles", month],
    queryFn: () => listCyclesFn({ data: { month_start: month } }),
  });

  const cycles = (cyclesQ.data ?? []) as CycleRow[];

  const detailQ = useQuery({
    enabled: Boolean(activeCycleId),
    queryKey: ["mp2-weekly-cycle", activeCycleId],
    queryFn: () => getCycleFn({ data: { cycle_id: activeCycleId! } }),
  });
  const detail = (detailQ.data ?? null) as CycleDetail | null;

  const dishesQ = useQuery({
    enabled: Boolean(detail?.plan_id ?? planId),
    queryKey: ["mp2-plan-dishes-admin", detail?.plan_id ?? planId],
    queryFn: () =>
      getPlanDishesFn({ data: { plan_id: (detail?.plan_id ?? planId)!, limit: 400 } }),
    staleTime: 5 * 60_000,
  });
  const dishesData = dishesQ.data as
    | { plan_id: string; dishes: Array<{ id: string; name: string; meal_slot: Slot; preference_compatibility: string[] | null }> }
    | { error: string }
    | undefined;
  const eligibleDishes = dishesData && "dishes" in dishesData ? dishesData.dishes : [];

  const dishesBySlot = useMemo(() => {
    const map: Record<Slot, typeof eligibleDishes> = {
      breakfast: [],
      lunch: [],
      snacks: [],
      dinner: [],
    };
    const pref = detail?.food_preference_code ?? preference;
    for (const d of eligibleDishes) {
      if (!SLOTS.includes(d.meal_slot)) continue;
      // The database only accepts a dish whose food-preference list contains
      // this week's preference, so the picker must offer exactly those dishes.
      if (pref) {
        const compat = Array.isArray(d.preference_compatibility)
          ? d.preference_compatibility
          : [];
        if (!compat.includes(pref)) continue;
      }
      map[d.meal_slot].push(d);
    }

    return map;
  }, [eligibleDishes, detail?.food_preference_code, preference]);

  const grid = useMemo(() => normaliseGrid(detail), [detail]);

  useEffect(() => {
    if (detailQ.error) toast.error((detailQ.error as Error).message);
  }, [detailQ.error]);
  useEffect(() => {
    if (dishesQ.error) toast.error((dishesQ.error as Error).message);
  }, [dishesQ.error]);

  const createMut = useMutation({
    mutationFn: (v: {
      plan_id: string;
      preference: string;
      week_start: string;
      month_label?: string;
      notes?: string;
      plan_title?: string;
      preference_label?: string;
    }) =>
      createHeaderFn({ data: v }) as Promise<
        { ok: true; header: { cycle_id?: string } | null } | { ok: false; error: string }
      >,
    onSuccess: (res, vars) => {
      if (!res || res.ok !== true) {
        const message =
          (res as { error?: string } | null)?.error ??
          "We couldn't create this weekly menu because of a system configuration problem. No changes were made. Please try again or contact support.";
        setCreateError(message);
        toast.error(message);
        return;
      }
      const header = res.header as { cycle_id?: string } | null;
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      setCreateError(null);
      setNewMonthLabel("");
      setNewNotes("");
      setNewWeekOpen(false);
      setMonth(monthStartOf(vars.week_start));
      if (header?.cycle_id) openEditor(header.cycle_id, "header");
      toast.success("New week created. You can now review its details.");
    },
    onError: (e) => {
      const message = (e as Error).message;
      setCreateError(message);
      toast.error(message);
    },
  });

  // Changing menu items can change the publish outcome, so any previous
  // validation message is cleared and must be recalculated on the next attempt.
  const addDishMut = useMutation({
    mutationFn: (v: { cycle_id: string; day_of_week: number; slot: Slot; dish_id: string }) =>
      addDishFn({ data: v }),
    onSuccess: (_r, v) => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", v.cycle_id] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const removeDishMut = useMutation({
    mutationFn: (v: { cycle_id: string; day_of_week: number; slot: Slot; dish_id: string }) =>
      removeDishFn({ data: v }),
    onSuccess: (_r, v) => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", v.cycle_id] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const reorderMut = useMutation({
    mutationFn: (v: { cycle_id: string; day_of_week: number; slot: Slot; dish_ids: string[] }) =>
      reorderDishesFn({ data: v }),
    onSuccess: (_r, v) => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", v.cycle_id] });
    },
    onError: (e) => toast.error((e as Error).message),
  });


  const exportItemsMut = useMutation({
    mutationFn: async (d: CycleDetail) => {
      const items = (await listItemsFn({ data: { cycle_id: d.cycle_id } })) as unknown as Array<{
        day_of_week: number;
        meal_slot: MenuItemSlot;
        dish_id: string;
        dish_name: string | null;
        sort_order: number;
      }>;
      exportMenuItems(
        {
          weekStart: d.week_start_date,
          planSlug: d.plan_slug ?? "plan",
          preference: d.food_preference_code,
        },
        items ?? [],
      );
      return items?.length ?? 0;
    },
    onSuccess: (n) => toast.success(`Exported ${n} menu item${n === 1 ? "" : "s"}`),
    onError: (e) => toast.error((e as Error).message),
  });

  const importItemsMut = useMutation({
    mutationFn: async (v: { file: File; detail: CycleDetail }) => {
      // A new import always starts from a clean feedback state.
      setImportState(null);
      // Fresh, complete, admin-authorised dish list for this plan. Any failure
      // or incomplete result stops the import here, before anything is saved.
      const lookup = await importCandidatesFn({ data: { plan_id: v.detail.plan_id } });
      const candidates = assembleImportCandidates({
        rows: lookup.rows,
        expectedCount: lookup.expectedCount,
        preference: v.detail.food_preference_code,
      });
      const parsed = await parseMenuItemsFile(v.file, {
        weekStart: v.detail.week_start_date,
        planSlug: v.detail.plan_slug ?? "",
        preference: v.detail.food_preference_code,
        dishes: candidates,
      });
      setImportState({
        cycleId: v.detail.cycle_id,
        errors: parsed.ok ? [] : parsed.errors,
        matches: parsed.matches,
        skipped: parsed.skipped,
        imported: null,
      });
      if (!parsed.ok) {
        throw new Error(`${parsed.errors.length} row problem(s) found — nothing was imported`);
      }
      // Anything already saved for the weekly off days stays exactly as it is:
      // the upload only replaces Monday–Friday items.
      const current = (await listItemsFn({ data: { cycle_id: v.detail.cycle_id } })) as Array<{
        day_of_week: number;
        meal_slot: string;
        dish_id: string;
        sort_order: number;
      }>;
      const keptWeeklyOff = (current ?? [])
        .filter((r) => isWeeklyOffDay(Number(r.day_of_week)))
        .map((r) => ({
          day_of_week: Number(r.day_of_week),
          meal_slot: r.meal_slot as (typeof SLOTS)[number],
          dish_id: r.dish_id,
          sort_order: Number(r.sort_order ?? 0),
        }));
      const res = await replaceItemsFn({
        data: { cycle_id: v.detail.cycle_id, items: [...parsed.items, ...keptWeeklyOff] },
      });
      return {
        res,
        cycleId: v.detail.cycle_id,
        imported: parsed.items.length,
        skippedCount: parsed.skipped.length,
        weeklyOffSkipped: parsed.weeklyOffSkipped,
      };
    },
    onSuccess: (out) => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", activeCycleId] });
      // Record how many rows actually landed so the panel can say "completed"
      // instead of "cancelled".
      setImportState((prev) =>
        prev && prev.cycleId === out.cycleId ? { ...prev, imported: out.imported } : prev,
      );

      const weeklyOffNote =
        out.weeklyOffSkipped > 0
          ? ` ${out.weeklyOffSkipped} Sunday row${out.weeklyOffSkipped === 1 ? "" : "s"} ignored: Sunday is the weekly off day.`
          : "";
      if (out.skippedCount > 0) {
        toast.warning(
          `Import completed with warnings. ${out.imported} row${out.imported === 1 ? "" : "s"} imported, ${out.skippedCount} row${out.skippedCount === 1 ? "" : "s"} skipped.` +
            weeklyOffNote,
        );
      } else {
        toast.success(
          `Import completed successfully. ${out.imported} row${out.imported === 1 ? "" : "s"} imported.` +
            weeklyOffNote,
        );
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Expected publish/withdraw validation messages are shown inline and stay on
  // screen until the next attempt. The message is tagged with the cycle AND the
  // area it was produced for, so a menu-items message can never appear against
  // another week or inside the header editor.
  const [actionError, setActionErrorState] = useState<
    { cycleId: string; message: string; area: "header" | "items" } | null
  >(null);
  const setActionError = useCallback(
    (v: { cycleId: string; message: string; area: "header" | "items" } | null) =>
      setActionErrorState(v),
    [],
  );
  const visibleError = (area: "header" | "items") =>
    actionError && activeCycleId && actionError.cycleId === activeCycleId && actionError.area === area
      ? actionError.message
      : null;
  const headerActionError = visibleError("header");
  const visibleActionError = visibleError("items");
  // Import feedback is shown only for the cycle it was produced for, and only in
  // the Menu Items editor.
  const visibleImport =
    importState && activeCycleId && importState.cycleId === activeCycleId && editorMode === "items"
      ? importState
      : null;

  // Header publication is a separate, admin-only state: it validates only the
  // week's own details and never touches the customer-facing menu publication.
  const publishHeaderFn = useServerFn(publishWeeklyCycleHeader);
  const withdrawHeaderFn = useServerFn(withdrawWeeklyCycleHeader);

  const publishHeaderMut = useMutation({
    mutationFn: (id: string) => publishHeaderFn({ data: { cycle_id: id } }),
    onMutate: () => setActionError(null),
    onSuccess: (res, id) => {
      if (res && res.ok === false) {
        setActionError({ cycleId: id, message: res.error, area: "header" });
        toast.error(res.error);
        return;
      }
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", id] });
      toast.success("Week details published. You can now add the menu items.");
    },
    onError: (_e, id) => {
      const m =
        "We couldn’t publish these week details because of a system problem. No changes were made. Please try again.";
      setActionError({ cycleId: id, message: m, area: "header" });
      toast.error(m);
    },
  });

  const withdrawHeaderMut = useMutation({
    mutationFn: (id: string) =>
      withdrawHeaderFn({ data: { cycle_id: id, note: "admin withdraw header" } }),
    onMutate: () => setActionError(null),
    onSuccess: (res, id) => {
      if (res && res.ok === false) {
        setActionError({ cycleId: id, message: res.error, area: "header" });
        toast.error(res.error);
        return;
      }
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", id] });
      toast.success("Week details withdrawn");
    },
    onError: (_e, id) => {
      const m =
        "We couldn’t withdraw these week details because of a system problem. No changes were made. Please try again.";
      setActionError({ cycleId: id, message: m, area: "header" });
      toast.error(m);
    },
  });


  const publishMut = useMutation({
    mutationFn: (id: string) => publishFn({ data: { cycle_id: id } }),
    onMutate: () => setActionError(null),
    onSuccess: (res, id) => {
      if (res && res.ok === false) {
        setActionError({ cycleId: id, message: res.error, area: "items" });
        toast.error(res.error);
        return;
      }
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", id] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", id] });
      toast.success("Menu published");
    },
    onError: (_e, id) => {
      const m =
        "We couldn’t publish this weekly menu because of a system problem. No changes were made. Please try again.";
      setActionError({ cycleId: id, message: m, area: "items" });
      toast.error(m);
    },
  });

  const scheduleMut = useMutation({
    mutationFn: (v: { id: string; when: string }) =>
      scheduleFn({ data: { cycle_id: v.id, publish_at: v.when } }),
    onMutate: () => setActionError(null),
    onSuccess: (res, v) => {
      if (res && res.ok === false) {
        setActionError({ cycleId: v.id, message: res.error, area: "items" });
        toast.error(res.error);
        return;
      }
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", v.id] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", v.id] });
      toast.success("Scheduled for publication");
    },
    onError: (_e, v) => {
      const m =
        "We couldn’t schedule this weekly menu because of a system problem. No changes were made. Please try again.";
      setActionError({ cycleId: v.id, message: m, area: "items" });
      toast.error(m);
    },
  });

  const withdrawMut = useMutation({
    mutationFn: (id: string) => withdrawFn({ data: { cycle_id: id, note: "admin withdraw" } }),
    onMutate: () => setActionError(null),
    onSuccess: (res, id) => {
      if (res && res.ok === false) {
        setActionError({ cycleId: id, message: res.error, area: "items" });
        toast.error(res.error);
        return;
      }
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", id] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", id] });
      toast.success("Cycle withdrawn");
    },
    onError: (_e, id) => {
      const m =
        "We couldn’t withdraw this weekly menu because of a system problem. No changes were made. Please try again.";
      setActionError({ cycleId: id, message: m, area: "items" });
      toast.error(m);
    },
  });




  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveFn({ data: { cycle_id: id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      if (activeCycleId === deleteTarget?.cycle_id) {
        setImportState(null);
        setActiveCycleId(null);
      }
      setDeleteTarget(null);
      toast.success("Weekly menu deleted (archived)");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const plans = (plansQ.data ?? []) as Array<{ id: string; title: string; slug: string }>;
  const prefs = (prefsQ.data ?? []) as Array<{
    code: string;
    label: string;
    is_active?: boolean;
  }>;
  // Only food preferences that are Active in the catalog may be offered as new
  // selections. Inactive ones stay in `prefs` so existing records can still be
  // labelled correctly, but they are never selectable.
  const activePrefs = prefs.filter((p) => p.is_active !== false);

  const canOpenEditor = Boolean(planId && preference && weekStart);
  const newCycleNamePreview =
    planId && weekStart
      ? `${plans.find((p) => p.id === planId)?.title ?? "Plan"} — ${weekStart}`
      : "";
  // Client-side duplicate warning (the database enforces this as well).
  const duplicateWeek =
    planId && preference && monthStartOf(weekStart) === month
      ? cycles.find(
          (c) =>
            c.plan_id === planId &&
            c.food_preference_code.toLowerCase() === preference.toLowerCase() &&
            c.week_start_date === weekStart,
        )
      : undefined;
  const longDay = (iso: string) =>
    new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  const duplicateWeekMessage = `This weekly menu already exists for ${
    plans.find((p) => p.id === planId)?.title ?? "the selected plan"
  } — ${prefs.find((p) => p.code === preference)?.label ?? preference ?? ""} for ${longDay(
    weekStart,
  )} to ${longDay(addDays(weekStart, 6))}. Please open the existing week instead of creating another one.`;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Weekly Menu"
        description="Plan, publish and withdraw the weekly Meal Plans 2.0 menu (Monday–Saturday; Sunday is the weekly off)."
        actions={
          <>
          <Button
            size="sm"
            onClick={() => {
              setNewMonthLabel("");
              setNewNotes("");
              setNewWeekOpen(true);
            }}
          >
            <CalendarDays className="mr-1 h-4 w-4" /> Add New Week
          </Button>
          <AdminDataToolbar
            exportName="weekly-menu"
            moduleId="weekly-menu"
            onImported={() => qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] })}
            rows={() =>
              cycles.map((c) => ({
                "Cycle Name": `${c.plan_title} — ${c.week_start_date}`,
                Plan: c.plan_slug,
                "Food Preference": c.food_preference_code,
                "Week Start (Mon)": c.week_start_date,
                "Week End (Sun)": c.week_end_date,
                "Month Label": c.month_label ?? "",
                Status: c.pub_status,
                Notes: "",
              }))
            }
          />
          </>
        }
      />


      {/* Calendar / month picker */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="month">Month</Label>
            <Input
              id="month"
              type="month"
              value={month.slice(0, 7)}
              onChange={(e) => setMonth(e.target.value + "-01")}
              className="w-40"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            {cyclesQ.isLoading
              ? "Loading…"
              : `${cycles.length} cycle${cycles.length === 1 ? "" : "s"} this month`}
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-2">Week</th>
                <th className="px-2 py-2">Plan</th>
                <th className="px-2 py-2">Preference</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Publish at</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((c) => (
                <tr
                  key={c.cycle_id}
                  data-active={c.cycle_id === activeCycleId ? "true" : undefined}
                  className="border-t border-border data-[active=true]:bg-primary/5"
                >
                  <td className="px-2 py-2 font-medium">
                    {c.week_start_date} → {c.week_end_date}
                  </td>
                  <td className="px-2 py-2">{c.plan_title}</td>
                  <td className="px-2 py-2">{c.food_preference_code}</td>
                  <td className="px-2 py-2">
                    <Badge variant={statusVariant(c.pub_status)}>{c.pub_status}</Badge>
                  </td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">
                    {c.publish_at ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant={c.cycle_id === activeCycleId ? "default" : "outline"}
                        onClick={() => openEditor(c.cycle_id)}
                      >
                        {c.cycle_id === activeCycleId ? "Editing" : "Edit"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setDeleteTarget(c)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!cyclesQ.isLoading && cycles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-2 py-4 text-muted-foreground">
                    No cycles for this month yet — create one below.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add a new week (header only) — compact modal */}
      <Dialog open={newWeekOpen} onOpenChange={setNewWeekOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Week</DialogTitle>
            <DialogDescription>
              Creates the week's details only. The day-by-day dishes are added afterwards under
              Edit Menu Items.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Plan</Label>
              <Select
                value={planId ?? ""}
                onValueChange={(v) => {
                  setCreateError(null);
                  setPlanId(v || null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Food preference</Label>
              <Select
                value={preference ?? ""}
                onValueChange={(v) => {
                  setCreateError(null);
                  setPreference(v || null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose preference" />
                </SelectTrigger>
                <SelectContent>
                  {activePrefs.map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Week start (Monday)</Label>
              <Input
                type="date"
                value={weekStart}
                onChange={(e) => {
                  const iso = e.target.value;
                  if (!iso) return;
                  setCreateError(null);
                  setWeekStart(isoMonday(new Date(iso + "T00:00:00Z")));
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Week end (Sunday, set automatically)</Label>
              <Input value={addDays(weekStart, 6)} readOnly disabled />
            </div>
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>Cycle Name (generated automatically)</Label>
              <Input
                value={newCycleNamePreview || "Choose a plan and week start to see the name"}
                readOnly
                disabled
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-month-label">Month label</Label>
              <Input
                id="new-month-label"
                value={newMonthLabel}
                onChange={(e) => setNewMonthLabel(e.target.value)}
                placeholder="September 2026"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Input value="draft (published later)" readOnly disabled />
            </div>
            <div className="grid gap-1.5 sm:col-span-2">
              <Label htmlFor="new-notes">Notes</Label>
              <Textarea
                id="new-notes"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                rows={2}
                placeholder="Internal notes for this week (optional)"
              />
            </div>
          </div>
          {createError ? (
            <Alert variant="destructive">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          ) : duplicateWeek ? (
            <Alert variant="destructive">
              <AlertDescription>{duplicateWeekMessage}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateError(null);
                setNewWeekOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!canOpenEditor || createMut.isPending}
              onClick={() =>
                createMut.mutate({
                  plan_id: planId!,
                  preference: preference!,
                  week_start: weekStart,
                  month_label: newMonthLabel.trim() || undefined,
                  notes: newNotes.trim() || undefined,
                  plan_title: plans.find((p) => p.id === planId)?.title,
                  preference_label: prefs.find((p) => p.code === preference)?.label,
                })
              }
            >
              {createMut.isPending ? "Creating…" : "Create Week"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor */}
      {activeCycleId ? (
        <div ref={editorRef} className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Editing cycle
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setActionError(null);
                setImportState(null);
                setActiveCycleId(null);
              }}

            >
              Close editor
            </Button>
          </div>
          {detailQ.isPending ? (
            <p className="text-sm text-muted-foreground">Loading cycle…</p>
          ) : detailQ.isError ? (
            <Alert variant="destructive">
              <AlertDescription>
                Could not load this cycle: {(detailQ.error as Error).message}
              </AlertDescription>
            </Alert>
          ) : !detail ? (
            <p className="text-sm text-muted-foreground">No cycle data.</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold">
                    {detail.plan_title ?? "Plan"} · {detail.food_preference_code}
                  </h2>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span>
                      {detail.week_start_date} → {detail.week_end_date} ·
                    </span>
                    <Badge variant={statusVariant(detail.pub_status)}>
                      {detail.pub_status}
                    </Badge>
                  </div>

                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={editorMode === "header" ? "default" : "outline"}
                    onClick={() => switchEditorMode("header")}
                  >
                    Edit Header
                  </Button>
                  <Button
                    size="sm"
                    variant={editorMode === "items" ? "default" : "outline"}
                    onClick={() => switchEditorMode("items")}

                  >
                    Edit Menu Items
                  </Button>
                </div>
              </div>

              {editorMode === "details" ? (
                <div className="grid gap-3 rounded-md border border-border bg-muted/20 p-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Cycle Name (system generated)</p>
                    <p className="font-medium">
                      {detail.plan_title ?? "Plan"} — {detail.week_start_date}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="font-medium">{detail.pub_status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Week</p>
                    <p className="font-medium">
                      {detail.week_start_date} → {detail.week_end_date}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Publish at</p>
                    <p className="font-medium">{detail.publish_at ?? "—"}</p>
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Choose <b>Edit Header</b> for the week's own details and publishing, or{" "}
                    <b>Edit Menu Items</b> for the day-by-day dishes.
                  </p>
                </div>
              ) : null}

              {editorMode === "header" ? (
                <HeaderEditor
                  cycleId={detail.cycle_id}
                  plans={plans}
                  prefs={prefs}
                  onIdentityChanged={() => {
                    qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
                    qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle", detail.cycle_id] });
                  }}
                  onPublishHeader={() => publishHeaderMut.mutate(detail.cycle_id)}
                  publishingHeader={publishHeaderMut.isPending}
                  onWithdrawHeader={() => withdrawHeaderMut.mutate(detail.cycle_id)}
                  withdrawingHeader={withdrawHeaderMut.isPending}
                  onEditMenuItems={() => switchEditorMode("items")}
                  actionError={headerActionError}
                />
              ) : null}


              {editorMode === "items" ? (
              <>
              <div className="mb-3 flex flex-wrap gap-2">
                {/* The customer-facing menu publication: not live → Publish Menu
                    (runs the full menu validation), live → Withdraw. */}
                {cycleActionForStatus(detail.pub_status) === "withdraw" ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="destructive" disabled={withdrawMut.isPending}>
                        <Trash2 className="mr-1 h-4 w-4" /> Withdraw
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Withdraw this weekly menu?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This unpublishes the menu for {detail.week_start_date} →{" "}
                          {detail.week_end_date}. It will no longer be visible to customers.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => withdrawMut.mutate(detail.cycle_id)}>
                          Withdraw
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : cycleActionForStatus(detail.pub_status) === "publish" ? (
                  <Button
                    size="sm"
                    onClick={() => publishMut.mutate(detail.cycle_id)}
                    disabled={publishMut.isPending}
                  >
                    <CheckCircle2 className="mr-1 h-4 w-4" />{" "}
                    {publishMut.isPending ? "Publishing…" : "Publish Menu"}
                  </Button>
                ) : null}

                <ScheduleButton
                  onSchedule={(when) => scheduleMut.mutate({ id: detail.cycle_id, when })}
                  disabled={scheduleMut.isPending}
                />
              </div>

              {visibleActionError ? (
                <div
                  role="alert"
                  className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {visibleActionError}
                </div>
              ) : null}





              {/* Menu items (dish-level) Excel controls — the header/cycle Excel
                  controls at the top of the page are unchanged. */}
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2">
                <span className="mr-1 text-xs font-medium text-muted-foreground">
                  Menu items Excel
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={exportItemsMut.isPending}
                  onClick={() => exportItemsMut.mutate(detail)}
                >
                  <Download className="mr-1 h-3.5 w-3.5" /> Export Menu Items
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => menuItemsFileRef.current?.click()}
                  disabled={importItemsMut.isPending}
                >
                  <Upload className="mr-1 h-3.5 w-3.5" />
                  {importItemsMut.isPending ? "Importing…" : "Import Menu Items"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    downloadMenuItemsTemplate({
                      weekStart: detail.week_start_date,
                      planSlug: detail.plan_slug ?? "",
                      preference: detail.food_preference_code,
                    })
                  }
                >
                  <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Menu Items Template
                </Button>
                <Button size="sm" variant="outline" onClick={downloadCompleteWeeklyMenuTemplate}>
                  <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Complete Weekly Menu Template
                </Button>
                <input
                  ref={menuItemsFileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) importItemsMut.mutate({ file: f, detail });
                  }}
                />
              </div>

              {visibleImport && visibleImport.errors.length > 0 ? (
                <Alert variant="destructive" className="mb-3">
                  <AlertDescription>
                    <p className="mb-1 font-medium">
                      Import cancelled — nothing was changed. Fix these rows and try again:
                    </p>
                    <ul className="list-disc pl-4 text-xs">
                      {visibleImport.errors.slice(0, 15).map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                      {visibleImport.errors.length > 15 ? (
                        <li>…and {visibleImport.errors.length - 15} more.</li>
                      ) : null}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}

              {visibleImport && visibleImport.imported !== null && visibleImport.skipped.length > 0 ? (
                <Alert className="mb-3">
                  <AlertDescription>
                    <p className="mb-1 font-medium">
                      Import completed with warnings. {visibleImport.imported} row
                      {visibleImport.imported === 1 ? "" : "s"} imported,{" "}
                      {visibleImport.skipped.length} row
                      {visibleImport.skipped.length === 1 ? "" : "s"} skipped.
                    </p>
                    <p className="mb-1 text-xs font-medium">Skipped rows:</p>
                    <ul className="list-disc pl-4 text-xs">
                      {visibleImport.skipped.slice(0, 15).map((s) => (
                        <li key={s.row_number}>{s.message}</li>
                      ))}
                      {visibleImport.skipped.length > 15 ? (
                        <li>…and {visibleImport.skipped.length - 15} more.</li>
                      ) : null}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}



              {visibleImport && visibleImport.matches.length > 0 ? (
                <div className="mb-3 rounded-md border border-border bg-muted/20 p-2">
                  <p className="mb-1 text-xs font-medium">
                    Dish matching summary —{" "}
                    {visibleImport.matches.filter((m) => m.status === "matched").length} matched,{" "}
                    {visibleImport.matches.filter((m) => m.status !== "matched").length} unresolved
                  </p>
                  <ul className="max-h-40 overflow-y-auto pl-1 text-xs text-muted-foreground">
                    {visibleImport.matches.slice(0, 30).map((m) => (
                      <li key={`${m.row_number}-${m.excel_name}`}>
                        Row {m.row_number}: “{m.excel_name || "(blank)"}” →{" "}
                        {m.status === "matched"
                          ? m.matched_name
                          : m.status === "ambiguous"
                            ? "duplicate dish names — resolve in Dish Management"
                            : m.status === "conflict"
                              ? "name and reference disagree"
                              : "not found"}
                      </li>
                    ))}
                    {visibleImport.matches.length > 30 ? (
                      <li>…and {visibleImport.matches.length - 30} more.</li>
                    ) : null}
                  </ul>
                </div>
              ) : null}

              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border px-2 py-1 text-left">Slot</th>
                      {DAYS.map((label, idx) => (
                        <th
                          key={label}
                          className={`border border-border px-2 py-1 text-left ${
                            isWeeklyOffDay(idx) ? "bg-muted/40" : ""
                          }`}
                        >
                          {label}
                          <div className="text-[10px] font-normal text-muted-foreground">
                            {addDays(detail.week_start_date, idx)}
                          </div>
                          {isWeeklyOffDay(idx) ? (
                            <div className="text-[10px] font-medium text-muted-foreground">
                              Weekly Off
                            </div>
                          ) : null}
                        </th>
                      ))}

                    </tr>
                  </thead>
                  <tbody>
                    {SLOTS.map((slot) => (
                      <tr key={slot}>
                        <td className="border border-border px-2 py-1 font-medium capitalize">
                          {slot}
                        </td>
                        {DAYS.map((_, dayIdx) => {
                          const list = grid[dayIdx][slot];
                          const options = dishesBySlot[slot];
                          const chosen = new Set(list.map((d) => d.dish_id));
                          const weeklyOff = isWeeklyOffDay(dayIdx);
                          const markedClosed = closedSlots.has(`${dayIdx}-${slot}`);
                          const closed = !weeklyOff && (markedClosed || list.length === 0);
                          return (
                            <td
                              key={`${slot}-${dayIdx}`}
                              className={`border border-border px-1 py-1 align-top ${
                                isWeeklyOffDay(dayIdx) ? "bg-muted/30" : ""
                              }`}
                            >

                              <div className="space-y-1">
                                {closed ? (
                                  <div className="flex items-center justify-between gap-1 rounded border border-dashed border-border px-1.5 py-1">
                                    <span className="text-[10px] text-muted-foreground">
                                      Closed / not serving
                                    </span>
                                    {markedClosed ? (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-5 px-1 text-[10px]"
                                        onClick={() => toggleSlotClosed(dayIdx, slot, false)}
                                      >
                                        Mark open
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : null}
                                {!weeklyOff && !markedClosed && list.length > 0 ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-full px-1 text-[10px] text-muted-foreground"
                                    onClick={() => toggleSlotClosed(dayIdx, slot, list.length > 0)}
                                  >
                                    Mark closed
                                  </Button>
                                ) : null}

                                {list.map((d, i) => (
                                  <div
                                    key={d.dish_id}
                                    className="flex items-center gap-1 rounded border border-border bg-background px-1.5 py-1"
                                  >
                                    <span className="flex-1 truncate text-xs" title={d.name ?? ""}>
                                      {d.name ?? "Dish"}
                                    </span>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5"
                                      disabled={i === 0 || reorderMut.isPending}
                                      aria-label="Move up"
                                      onClick={() =>
                                        reorderMut.mutate({
                                          cycle_id: detail.cycle_id,
                                          day_of_week: dayIdx,
                                          slot,
                                          dish_ids: moveItem(list.map((x) => x.dish_id), i, i - 1),
                                        })
                                      }
                                    >
                                      <ArrowUp className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5"
                                      disabled={i === list.length - 1 || reorderMut.isPending}
                                      aria-label="Move down"
                                      onClick={() =>
                                        reorderMut.mutate({
                                          cycle_id: detail.cycle_id,
                                          day_of_week: dayIdx,
                                          slot,
                                          dish_ids: moveItem(list.map((x) => x.dish_id), i, i + 1),
                                        })
                                      }
                                    >
                                      <ArrowDown className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5 text-destructive"
                                      aria-label="Remove dish"
                                      onClick={() =>
                                        removeDishMut.mutate({
                                          cycle_id: detail.cycle_id,
                                          day_of_week: dayIdx,
                                          slot,
                                          dish_id: d.dish_id,
                                        })
                                      }
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                                {markedClosed ? null : (
                                <Select
                                  value=""
                                  onValueChange={(v) => {
                                    if (!v) return;
                                    addDishMut.mutate({
                                      cycle_id: detail.cycle_id,
                                      day_of_week: dayIdx,
                                      slot,
                                      dish_id: v,
                                    });
                                  }}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="+ Add dish" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {options
                                      .filter((d) => !chosen.has(d.id))
                                      .map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                          {d.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                                )}

                              </div>
                            </td>
                          );
                        })}

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-xs text-muted-foreground">
                Each Monday–Saturday meal slot can be open (serving) or closed (not serving). An
                empty slot counts as closed and does not block publishing — every operating day
                only needs at least one open slot with a dish that is valid for this plan and food
                preference. Sunday is the weekly off day. Cycle Name is generated
                automatically from the plan and week start date.

              </p>
              </>
              ) : null}
            </>

          )}
        </div>
      ) : (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Pick a plan, preference and Monday week start, then choose <b>Open editor</b> to
            build the 7-day grid. Existing cycles can be opened from the table above.
          </AlertDescription>
        </Alert>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this weekly menu?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the <strong>{deleteTarget?.plan_title}</strong>{" "}
              <strong>{deleteTarget?.food_preference_code}</strong> menu for{" "}
              <strong>
                {deleteTarget?.week_start_date} – {deleteTarget?.week_end_date}
              </strong>
              ?
              <br />
              <br />
              This will archive the configuration. Historical orders and subscriptions will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && archiveMut.mutate(deleteTarget.cycle_id)}
            >
              {archiveMut.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ScheduleButton({
  onSchedule,
  disabled,
}: {
  onSchedule: (whenIso: string) => void;
  disabled?: boolean;
}) {
  const [when, setWhen] = useState<string>("");
  return (
    <div className="flex items-center gap-2">
      <Input
        type="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        className="h-8 w-52 text-xs"
      />
      <Button
        size="sm"
        variant="secondary"
        disabled={!when || disabled}
        onClick={() => onSchedule(new Date(when).toISOString())}
      >
        <Clock className="mr-1 h-4 w-4" /> Schedule
      </Button>
    </div>
  );
}
type CycleHeader = {
  cycle_id: string;
  name: string;
  plan_id: string;
  plan_title: string;
  food_preference_code: string;
  week_start_date: string;
  week_end_date: string;
  month_label: string | null;
  notes: string | null;
  pub_status: string;
  publish_at: string | null;
  header_published_at?: string | null;
};

/**
 * Header (cycle-level) editor. Plan, food preference, week start, month label
 * and notes are editable; week end follows the Monday week start and Cycle Name
 * stays system generated. Identity changes are validated in the database for
 * duplicates and for dishes that would become invalid.
 *
 * Publishing here publishes the week's own details only: it never checks dishes
 * and never makes the week visible to customers. The customer-facing menu is
 * published separately under Edit Menu Items.
 */
function HeaderEditor({
  cycleId,
  plans,
  prefs,
  onIdentityChanged,
  onPublishHeader,
  publishingHeader,
  onWithdrawHeader,
  withdrawingHeader,
  onEditMenuItems,
  actionError,
}: {
  cycleId: string;
  plans: Array<{ id: string; title: string; slug: string }>;
  prefs: Array<{ code: string; label: string; is_active?: boolean }>;
  onIdentityChanged: () => void;
  onPublishHeader: () => void;
  publishingHeader?: boolean;
  onWithdrawHeader: () => void;
  withdrawingHeader?: boolean;
  onEditMenuItems: () => void;
  actionError?: string | null;
}) {
  const qc = useQueryClient();
  const getHeaderFn = useServerFn(getWeeklyCycleHeader);
  const saveHeaderFn = useServerFn(updateWeeklyCycleHeader);
  const saveIdentityFn = useServerFn(updateWeeklyCycleIdentity);

  const headerQ = useQuery({
    queryKey: ["mp2-weekly-cycle-header", cycleId],
    queryFn: () => getHeaderFn({ data: { cycle_id: cycleId } }),
  });
  const header = (headerQ.data ?? null) as CycleHeader | null;

  const [notes, setNotes] = useState<string>("");
  const [monthLabel, setMonthLabel] = useState<string>("");
  const [planId, setPlanId] = useState<string>("");
  const [preference, setPreference] = useState<string>("");
  const [weekStart, setWeekStart] = useState<string>("");
  // Expected save-time validation messages (duplicate week, dishes that would
  // become invalid, …) are shown inline here so the editor stays open and the
  // typed values are preserved. Genuine unexpected failures still toast.
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (header) {
      setNotes(header.notes ?? "");
      setMonthLabel(header.month_label ?? "");
      setPlanId(header.plan_id ?? "");
      setPreference(header.food_preference_code ?? "");
      setWeekStart(header.week_start_date ?? "");
    }
  }, [header?.cycle_id, header?.notes, header?.month_label]);

  const identityChanged =
    Boolean(header) &&
    (planId !== header!.plan_id ||
      preference !== header!.food_preference_code ||
      weekStart !== header!.week_start_date);

  const saveMut = useMutation({
    mutationFn: async (): Promise<{ ok: boolean; error?: string }> => {
      // Plan / preference / week start first: if that validation fails nothing
      // at all is changed, so the other details are not saved either. The
      // server returns a structured result for expected validation failures.
      if (identityChanged) {
        const res = (await saveIdentityFn({
          data: { cycle_id: cycleId, plan_id: planId, preference, week_start: weekStart },
        })) as { ok?: boolean; error?: string } | null;
        if (res && res.ok === false) {
          return { ok: false, error: res.error ?? "" };
        }
      }
      await saveHeaderFn({ data: { cycle_id: cycleId, notes, month_label: monthLabel } });
      return { ok: true };
    },
    onSuccess: (res) => {
      if (!res.ok) {
        // Expected validation failure — keep the editor open, keep the typed
        // values, show the exact server message inline.
        setSaveError(
          res.error && res.error.length < 1000
            ? res.error
            : "We couldn’t save the week details. No changes were made. Please try again.",
        );
        return;
      }
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycle-header", cycleId] });
      qc.invalidateQueries({ queryKey: ["mp2-weekly-cycles"] });
      onIdentityChanged();
      toast.success("Header details saved");
    },
    // Unexpected failures (network, permissions) still land here and are shown
    // inline as well — never in the route error boundary.
    onError: (e) => {
      const msg = (e as Error)?.message ?? "";
      setSaveError(
        msg && msg.length < 600
          ? msg
          : "We couldn’t save the week details. No changes were made. Please try again.",
      );
    },
  });


  const weekEndOk = header
    ? addDays(header.week_start_date, 6) === header.week_end_date
    : true;
  const weekStartIsMonday =
    !weekStart || new Date(weekStart + "T00:00:00Z").getUTCDay() === 1;

  if (headerQ.isPending) {
    return <p className="text-sm text-muted-foreground">Loading header…</p>;
  }
  if (headerQ.isError || !header) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Could not load this week's details. Please close the editor and try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4 rounded-md border border-border bg-muted/20 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label>Cycle Name (generated automatically)</Label>
          <Input value={header.name} readOnly disabled />
        </div>
        <div className="grid gap-1.5">
          <Label>Header status</Label>
          <Input
            value={header.header_published_at ? "published" : "not published"}
            readOnly
            disabled
          />
        </div>
        <div className="grid gap-1.5 sm:col-span-2">
          <Label>Customer menu status (set under Edit Menu Items)</Label>
          <Input value={header.pub_status} readOnly disabled />
        </div>

        <div className="grid gap-1.5">
          <Label>Plan</Label>
          <Select
            value={planId}
            onValueChange={(v) => {
              setPlanId(v);
              setSaveError(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>Food preference</Label>
          <Select
            value={preference}
            onValueChange={(v) => {
              setPreference(v);
              setSaveError(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose preference" />
            </SelectTrigger>
            <SelectContent>
              {prefs
                .filter(
                  (p) =>
                    // Active preferences are selectable. An inactive one is only
                    // listed (disabled) when this week already uses it, so the
                    // saved value stays visible without becoming selectable.
                    p.is_active !== false || p.code === header?.food_preference_code,
                )
                .map((p) => (
                  <SelectItem
                    key={p.code}
                    value={p.code}
                    disabled={p.is_active === false}
                  >
                    {p.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="header-week-start">Week start (Monday)</Label>
          <Input
            id="header-week-start"
            type="date"
            value={weekStart}
            onChange={(e) => {
              const iso = e.target.value;
              if (!iso) return;
              setWeekStart(isoMonday(new Date(iso + "T00:00:00Z")));
              setSaveError(null);
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Week end (Sunday, set automatically)</Label>
          <Input value={weekStart ? addDays(weekStart, 6) : (header.week_end_date ?? "")} readOnly disabled />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="month-label">Month label</Label>
          <Input
            id="month-label"
            value={monthLabel}
            onChange={(e) => {
              setMonthLabel(e.target.value);
              setSaveError(null);
            }}
            placeholder="September 2026"
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Publish at</Label>
          <Input value={header.publish_at ?? "—"} readOnly disabled />
        </div>
        <div className="grid gap-1.5 sm:col-span-2">
          <Label htmlFor="cycle-notes">Notes</Label>
          <Textarea
            id="cycle-notes"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setSaveError(null);
              }}
            rows={3}
            placeholder="Internal notes for this week"
          />
        </div>
      </div>

      {saveError ? (
        <Alert variant="destructive">
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      ) : null}

      {!weekEndOk ? (
        <Alert variant="destructive">
          <AlertDescription>
            Week end must be the Sunday immediately following the week start. Saving the header
            will correct it.
          </AlertDescription>
        </Alert>
      ) : null}

      {!weekStartIsMonday ? (
        <Alert variant="destructive">
          <AlertDescription>Week start must be a Monday. Please select a Monday.</AlertDescription>
        </Alert>
      ) : identityChanged ? (
        <Alert>
          <AlertDescription>
            You are changing the plan, food preference or week for this week. It will be saved
            only if no other week already uses the same combination and every dish already saved
            here stays valid.
          </AlertDescription>
        </Alert>
      ) : null}

      {actionError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {actionError}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">

        <Button
          size="sm"
          variant="outline"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !weekStartIsMonday}
        >
          {saveMut.isPending ? "Saving…" : "Save Header"}
        </Button>
        {/* Header publication only. A week's details can be published with no
            dishes at all; the customer-facing menu is published separately. */}
        {header.header_published_at ? (
          <>
            <Button size="sm" variant="outline" onClick={onEditMenuItems}>
              Edit Menu Items
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" disabled={withdrawingHeader}>
                  <Trash2 className="mr-1 h-4 w-4" /> Withdraw Header
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw these week details?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This only un-publishes the details of {header.week_start_date} →{" "}
                    {header.week_end_date} for internal use. It does not change what customers
                    see; the menu itself is published and withdrawn under Edit Menu Items.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onWithdrawHeader}>
                    Withdraw Header
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : (
          <Button size="sm" onClick={onPublishHeader} disabled={publishingHeader}>
            <CheckCircle2 className="mr-1 h-4 w-4" />{" "}
            {publishingHeader ? "Publishing…" : "Publish Header"}
          </Button>
        )}
      </div>


      <p className="text-xs text-muted-foreground">
        Publishing the header publishes the week's own details only — no dishes are needed and
        customers see nothing yet. Add the dishes under Edit Menu Items and publish the menu there
        to make the week visible to customers. Cycle Name is generated from the plan and week start
        date. Week end always follows the Monday week start.
      </p>

    </div>
  );
}
